import { type CallingCountryCode } from '@wix/form-validator';
import { useState, useEffect, useMemo, useCallback } from 'react';

import {
  parseCountryCodeFromPhoneNumber,
  parseLocalPhone,
} from './phone-input-utils';

interface UsePhoneInputLogicParams {
  value: string | null | undefined;
  defaultCountryCode: CallingCountryCode | undefined;
  allowedCountryCodes: CallingCountryCode[] | undefined;
  onChange: (value: string | null) => void;
}

interface UsePhoneInputLogicReturn {
  currentValue: string;
  selectedCountryCode: CallingCountryCode | undefined;
  handlePhoneChange: (newValue: string) => void;
  handleCountryCodeChange: (countryCode: CallingCountryCode) => void;
}

// TODO: use this for WDS and WUT fields
export function usePhoneInputLogic({
  value,
  defaultCountryCode,
  allowedCountryCodes,
  onChange,
}: UsePhoneInputLogicParams): UsePhoneInputLogicReturn {
  const [currentValue, setCurrentValue] = useState(value || '');
  const [selectedCountryCode, setSelectedCountryCode] = useState<
    CallingCountryCode | undefined
  >(defaultCountryCode);

  const countryCodeFromPhoneNumber = useMemo(
    () => parseCountryCodeFromPhoneNumber(currentValue),
    [currentValue],
  );

  // Sync with parent value while current value is falsy
  useEffect(() => {
    if (value && !currentValue) {
      setCurrentValue(value);
    }
  }, [value, currentValue]);

  // Sync selected country code with default or detected country code
  useEffect(() => {
    if (defaultCountryCode && !selectedCountryCode) {
      setSelectedCountryCode(defaultCountryCode);
    }
  }, [defaultCountryCode, selectedCountryCode]);

  // Update country code when detected from phone number
  useEffect(() => {
    setSelectedCountryCode(defaultCountryCode ?? countryCodeFromPhoneNumber);
  }, [countryCodeFromPhoneNumber, defaultCountryCode]);

  const handlePhoneChange = useCallback(
    (newValue: string) => {
      setCurrentValue(newValue);

      if (!newValue) {
        onChange(null);
        return;
      }

      const countryCodeByPhoneNumber =
        parseCountryCodeFromPhoneNumber(newValue);

      setSelectedCountryCode((latestCountryCode) => {
        const countryToUse =
          countryCodeByPhoneNumber ||
          latestCountryCode ||
          (allowedCountryCodes?.length === 1
            ? allowedCountryCodes[0]
            : undefined);

        const parsedValue = parseLocalPhone(newValue, countryToUse);
        onChange(parsedValue);

        return countryCodeByPhoneNumber || latestCountryCode;
      });
    },
    [allowedCountryCodes, onChange],
  );

  const handleCountryCodeChange = useCallback(
    (countryCode: CallingCountryCode) => {
      setSelectedCountryCode(countryCode);

      setCurrentValue((latestValue) => {
        const parsedValue = parseLocalPhone(latestValue, countryCode);
        onChange(parsedValue);
        return latestValue;
      });
    },
    [onChange],
  );

  return {
    currentValue,
    selectedCountryCode,
    handlePhoneChange,
    handleCountryCodeChange,
  };
}
